import { useCallback,useState } from "react";
import { Pagination } from "@mui/material";
import debounce from "lodash/debounce";
import { Loader2 } from "lucide-react";

import { ConsultationPreview } from "components/ConsultationPreview/ConsultationPreview";
import { SearchInput } from "components/ui";
import { useMyConsultationsPaginatedList } from "features/consultations/hooks/useConsultationsPaginatedList";
import { usePracticeServices } from "features/practice/hooks/usePracticeServices";
import { createDateFromArray } from "utils/createDateFromArray";

import styles from "./ConsultationsTable.module.css";

export const MyConsultationsTable = ({
  goToPatientConsult,
  itemsPerPage = 10,
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);
  const { data, isLoading } = useMyConsultationsPaginatedList({
    page,
    search: debouncedSearch,
    rows: rowsPerPage,
  });
  const { data: services } = usePracticeServices();

  const totalPages = Math.ceil(data?.totalRecords / rowsPerPage);
  const start = (page - 1) * rowsPerPage + 1;
  const end = Math.min(page * rowsPerPage, data?.totalRecords);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce((query) => {
      setDebouncedSearch(query);
    }, 300),
    []
  );

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <div className={styles.emptyState}>
          <Loader2 className={styles.loadingSpinner} size={24} />
          <p className={styles.emptyStateTitle}>Loading data...</p>
          <p className={styles.emptyStateText}>
            Please wait while we fetch the consultations.
          </p>
        </div>
      );
    }

    if (!data?.data?.length) {
      return (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateTitle}>No consultations found</p>
          <p className={styles.emptyStateText}>
            {search
              ? "Try adjusting your search to find what you're looking for."
              : "There are no consultations to display at this time."}
          </p>
        </div>
      );
    }

    return (
      <>
        <div className={styles.consultationPreviewWrapper}>
          {(data?.data || [])?.map((consultation) => (
            <ConsultationPreview
              goToPatientConsult={goToPatientConsult}
              consultation={{
                id: consultation.id,
                title:
                  services.find(
                    ({ id }) => id === consultation.mainPracticeServiceId
                  )?.name || "Consultation",
                date: createDateFromArray(consultation.created),
                consultationStaff: consultation.consultationStaff,
                patient: consultation.patient,
                attachments: {
                  prescriptions: consultation?.patientPrescriptions || [],
                  refferrals: [],
                },
              }}
              text={
                consultation.consultationNoteList?.at(0)?.note ||
                "No notes found for this consultation."
              }
              key={consultation.id}
            >
              {consultation.consultationNoteList?.at(0)?.createdByStaffMember
                ?.firstName && (
                <div className={styles.consultationDoctorSection}>
                  <h3>Doctor</h3>
                  <p>{`${
                    consultation.consultationNoteList?.at(0)
                      ?.createdByStaffMember?.firstName
                  } ${
                    consultation.consultationNoteList?.at(0)
                      ?.createdByStaffMember?.middleNames || ""
                  } ${
                    consultation.consultationNoteList?.at(0)
                      ?.createdByStaffMember?.lastName || ""
                  }`}</p>
                </div>
              )}
            </ConsultationPreview>
          ))}
        </div>
        <div className={styles.paginationWrapper}>
          <p className={styles.resultsInfo}>
            {start}-{end} of {data?.totalRecords} results
          </p>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
            style={{ marginRight: "8px" }}
          >
            {[10, 20, 50, 100].map((opt) => (
              <option key={opt} value={opt}>{opt} / page</option>
            ))}
          </select>
          <Pagination
            count={totalPages}
            onChange={(_event, value) => {
              setPage(value);
            }}
            page={page}
          />
        </div>
      </>
    );
  };

  return (
    <div className={styles.tableWarpper}>
      <div className={styles.table}>
        <div className={styles.tableTopBarWrapper}>
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              debouncedSearchHandler(value);
            }}
            placeholder="Search consultations"
          />
        </div>
        {renderTableContent()}
      </div>
    </div>
  );
};
